import { GridRowParams } from "@mui/x-data-grid-premium";
import { useCallback, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";

import { UnifiedWorkRow } from "./types";

/**
 * Search param that mirrors the open work drawer: `?workflowId=<uuid>`. Same
 * contract as the exceedance dashboard's `?drawer=` — opening pushes a history
 * entry so Back closes the drawer, and the URL is shareable. The param is the
 * single source of truth for what's open.
 */
export const WORK_DRAWER_PARAM = "workflowId";

export type WorkDrawerState = {
  /** Non-null when the drawer is open. */
  workflowId: string | null;
  /** The loaded row for {@link workflowId}, or null when it isn't on this page. */
  row: UnifiedWorkRow | null;
  /** Feed the grid's loaded page in; the drawer renders from it. */
  onRowsLoaded: (items: UnifiedWorkRow[]) => void;
  openRow: (params: GridRowParams<UnifiedWorkRow>) => void;
  close: () => void;
};

/**
 * URL-backed open/close state for the work queue's detail drawer. Every field
 * the drawer shows comes from the grid query's own results, so the loaded page
 * is cached here instead of refetched by workflow id; the drawer's comment
 * thread is the one thing it fetches for itself.
 */
export function useWorkDrawer(): WorkDrawerState {
  const [searchParams, setSearchParams] = useSearchParams();
  const workflowId = searchParams.get(WORK_DRAWER_PARAM);

  const [loadedRows, setLoadedRows] = useState<UnifiedWorkRow[]>([]);
  const onRowsLoaded = useCallback((items: UnifiedWorkRow[]) => setLoadedRows(items), []);

  const row = useMemo(
    () => loadedRows.find((candidate) => candidate.workflowId === workflowId) ?? null,
    [loadedRows, workflowId]
  );

  const openRow = useCallback(
    (params: GridRowParams<UnifiedWorkRow>) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set(WORK_DRAWER_PARAM, params.row.workflowId);
          return next;
        },
        // Opening PUSHES so the browser Back button lands on the param-less URL
        // and closes the drawer; preventScrollReset keeps the grid where it was.
        { preventScrollReset: true }
      );
    },
    [setSearchParams]
  );

  const close = useCallback(() => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete(WORK_DRAWER_PARAM);
        return next;
      },
      // Closing REPLACES: going Back after an explicit close shouldn't reopen.
      { replace: true, preventScrollReset: true }
    );
  }, [setSearchParams]);

  return { workflowId, row, onRowsLoaded, openRow, close };
}
